import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useIndeedFlexClientsQuery } from 'queries/admin/indeedFlex/useIndeedFlexClientsQuery'
import { useIndeedFlexJobShiftsQuery } from 'queries/admin/indeedFlex/useIndeedFlexJobShiftsQuery'
import { useIndeedFlexJobShiftsFiltersQueryState } from '../hooks/useIndeedFlexJobShiftsFiltersQueryState'

type IndeedFlexJobShiftsContextState = {
  state: {
    clients: NonNullable<
      ReturnType<typeof useIndeedFlexClientsQuery>['data']
    >['items']
    filters: {
      ifJobId: number | null
    }
    data: ReturnType<typeof useIndeedFlexJobShiftsQuery>['data']
    dataUpdatedAt: number
    isPending: boolean
    isFetching: boolean
    isError: boolean
    error: string
    hasSearchParams: boolean
  }
  actions: {
    setFilters: ReturnType<typeof useIndeedFlexJobShiftsFiltersQueryState>[1]
    refetch: () => void
  }
}

const [Context, useIndeedFlexJobShiftsContextValue] =
  createContext<IndeedFlexJobShiftsContextState>({
    name: 'IndeedFlexJobShiftsContext',
  })

export const IndeedFlexJobShiftsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data: clients } = useIndeedFlexClientsQuery()
  const [filters, setFilters] = useIndeedFlexJobShiftsFiltersQueryState()

  const ifJobId = filters.ifJobId
  const hasSearchParams = ifJobId !== null

  const request = ifJobId !== null ? { ifJobId } : null

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useIndeedFlexJobShiftsQuery(request)

  const value: IndeedFlexJobShiftsContextState = {
    state: {
      clients: clients?.items ?? [],
      filters,
      data,
      dataUpdatedAt,
      isPending: hasSearchParams && isPending,
      isFetching,
      isError: hasSearchParams && isError,
      error: parseErrorMessage(error),
      hasSearchParams,
    },
    actions: {
      setFilters,
      refetch,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useIndeedFlexJobShiftsContextValue }
